import { useState, useCallback } from 'react';
import { bind } from 'bind-event-listener';

import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';

export const useNBMFocus = (nbmRef: React.RefObject<HTMLElement>) => {
	const [nbmFocused, setNbmFocused] = useState(false);

	const onNbmFocus = useCallback(() => {
		setNbmFocused(true);
	}, []);

	const onNbmBlur = useCallback(
		(event: FocusEvent) => {
			// 0 timeout to allow the activeElement to be updated (blur tends to happen first)
			setTimeout(() => {
				const inNbm = nbmRef.current?.contains(window.document.activeElement);
				const inToolbar = window.document.activeElement?.closest('[role="toolbar"]');
				const inPopup = window.document.activeElement?.closest('[data-editor-popup]:not(mark)');

				const newToolbarEnabled =
					fg('aifc_create_enabled') ||
					expValEquals('platform_editor_toolbar_aifc_confluence', 'isEnabled', true);
				const inPortal = newToolbarEnabled
					? window.document.activeElement?.closest('.atlaskit-portal')
					: null;

				// If in toolbar or popup, on blur of the nbm start listening for subsequent blurs
				// in the toolbar or popup to determine if the focus is still within the nbm to
				// determine when the focus has "truly" left the nbm
				for (const node of [inToolbar, inPopup, inPortal]) {
					if (!node) {
						continue;
					}

					let target: Node | null = null;

					// relatedTarget can be null when a popup closes from inside the popup itself
					// and reopens, like when going from the top of the list to the bottom
					if (event.relatedTarget === null && inPopup) {
						target = window.document.activeElement;
					}

					if (event.relatedTarget instanceof Node && node.contains(event.relatedTarget)) {
						target = event.relatedTarget;
					}

					// if the active element is in a portal and the related target is a toolbar, use the related target as the target
					if (
						inPortal &&
						event.relatedTarget instanceof HTMLElement &&
						event.relatedTarget.closest('[role="toolbar"]')
					) {
						target = event.relatedTarget;
					}

					if (target) {
						const unbind = bind(target, {
							type: 'blur',
							listener: (e) => {
								// unbind and then recursively bind to the next related target. Gross but it works
								unbind();
								if (e instanceof FocusEvent) {
									onNbmBlur(e);
								}
							},
							options: { capture: true },
						});

						return;
					}
				}

				// If a blur and focus happen within the nbm, ignore
				if (inNbm) {
					return;
				}

				setNbmFocused(false);
			}, 0);
		},
		[nbmRef],
	);

	return { nbmFocused, onNbmFocus, onNbmBlur };
};
